import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useUserRolePermissions } from 'hooks';
import { format } from 'date-fns';
import { capitalizeSingleWord } from 'utils/helpers/format';

const buttonStyles = {
  '&, :hover': {
    boxShadow: 'none',
  },
  height: 33,
  width: 130,
};

const LoadCard = ({
  driver,
  load,
  onDelete,
  onDuplicate,
  onEdit,
  onFavorite,
  onUnfavorite,
  onView,
  subheader,
  title,
}) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const [menuAnchor, setMenuAnchor] = useState();

  const createdDateDisplay = format(new Date(load.createdDate), 'MM/dd/yyyy');
  const updatedDateDisplay = format(new Date(load.updatedDate), 'MM/dd/yyyy');

  const isCompleted = R.equals(load.status, 'COMPLETED');
  const isDraft = R.equals(load.status, 'DRAFT');
  const isScheduled = R.equals(load.status, 'SCHEDULED');
  const isPending = R.equals(load.status, 'PENDING');
  const isWarning = R.equals(load.status, 'WARNING');
  const { isFavorite } = load;

  const statusBoxStyles = {
    alignItems: 'center',
    backgroundColor: isCompleted
      ? 'success.light'
      : isDraft
      ? 'surface.medium'
      : isScheduled
      ? 'primary.lighter'
      : isPending
      ? 'warning.light'
      : 'error.light', // warning
    borderRadius: 0.5,
    gridColumn: '1/3',
    height: 61,
    justifyContent: 'center',
    mb: 3,
    pb: 1,
    pt: 1,
  };

  const statusStyles = {
    color: isWarning ? 'error.dark' : 'text.primary',
  };

  // prettier-ignore
  const handleMenuOpen = R.compose(
    setMenuAnchor, 
    R.prop('currentTarget')
  );

  const handleView = () => {
    onView(load.loadId);
    setMenuAnchor();
  };

  const handleDuplicate = () => {
    onDuplicate(load.loadId);
    setMenuAnchor();
  };

  const handleEdit = () => {
    onEdit(load.loadId);
    setMenuAnchor();
  };

  const handleDelete = () => {
    onDelete({ driver, load });
    setMenuAnchor();
  };

  const handleFavorite = () => {
    onFavorite({ loadId: load.loadId });
    setMenuAnchor();
  };

  const handleUnfavorite = () => {
    onUnfavorite({ loadId: load.loadId });
    setMenuAnchor();
  };

  // prettier-ignore
  const favoriteIcon = load.isFavorite 
    ? <StarIcon color="secondary" />
    : <StarBorderIcon color="secondary" />;

  const action = (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <IconButton
        disabled={
          !isDispatchingManage ||
          ['draft', 'pending', 'warning'].includes(load.status?.toLowerCase())
        }
        onClick={load.isFavorite ? handleUnfavorite : handleFavorite}
        style={{ pointerEvents: isDispatchingManage ? 'default' : 'none' }}
      >
        {favoriteIcon}
      </IconButton>
      <IconButton onClick={handleMenuOpen}>
        <MoreVertIcon />
      </IconButton>
    </Stack>
  );

  return (
    <Card sx={{ boxShadow: 6, width: 310 }}>
      <CardHeader
        action={action}
        slotProps={{
          subheader: { variant: 'body2' },
          title: { variant: 'h6' },
        }}
        subheader={subheader}
        sx={{ p: 2, pb: 0 }}
        title={title}
      />
      <CardContent
        sx={{ display: 'grid', gridColumnGap: 16, gridRowGap: 8, gridTemplateColumns: '1fr 1fr' }}
      >
        <Divider />
        <Divider />
        <Typography sx={{ color: 'secondary', textAlign: 'center' }} variant="body2">
          Created
        </Typography>
        <Typography sx={{ color: 'secondary', textAlign: 'center' }} variant="body2">
          Last Edited
        </Typography>
        <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h6">
          {createdDateDisplay}
        </Typography>
        <Typography sx={{ mb: 2, textAlign: 'center' }} variant="h6">
          {updatedDateDisplay}
        </Typography>

        <Stack sx={statusBoxStyles}>
          <Typography sx={statusStyles} variant="subtitle1">
            {capitalizeSingleWord(load.status)}
          </Typography>
          <Typography variant="body2">PO# {load.poNumber}</Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          sx={{ gridColumn: '1 / span 2', justifyContent: 'center' }}
        >
          {isDispatchingManage && isCompleted && (
            <Button onClick={handleDuplicate} sx={buttonStyles} variant="outlined">
              Duplicate
            </Button>
          )}
          {isDispatchingManage && !isCompleted && (
            <Button onClick={handleEdit} sx={buttonStyles} variant="outlined">
              Edit
            </Button>
          )}
          <Button onClick={handleView} sx={buttonStyles} variant="contained">
            View
          </Button>
        </Stack>
      </CardContent>
      {menuAnchor && (
        <Menu anchorEl={menuAnchor} onClose={() => setMenuAnchor()} open>
          <MenuItem onClick={handleView}>View</MenuItem>
          {isDispatchingManage && isCompleted && (
            <MenuItem onClick={handleDuplicate}>Duplicate</MenuItem>
          )}
          {isDispatchingManage && !isCompleted && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
          {isDispatchingManage && !isCompleted && (
            <MenuItem onClick={handleDelete}>Delete</MenuItem>
          )}
          {isDispatchingManage && !isFavorite && (
            <MenuItem
              disabled={
                !isDispatchingManage ||
                ['draft', 'pending', 'warning'].includes(load.status?.toLowerCase())
              }
              onClick={handleFavorite}
            >
              Add to Favorites
            </MenuItem>
          )}
          {isDispatchingManage && isFavorite && (
            <MenuItem onClick={handleUnfavorite}>Remove from Favorites</MenuItem>
          )}
        </Menu>
      )}
    </Card>
  );
};

LoadCard.propTypes = {
  driver: PropTypes.shape({}),
  load: PropTypes.shape({
    createdDate: PropTypes.string,
    isFavorite: PropTypes.bool,
    loadId: PropTypes.string,
    poNumber: PropTypes.string,
    status: PropTypes.oneOf(['DRAFT', 'PENDING', 'SCHEDULED', 'COMPLETED', 'WARNING']),
    updatedDate: PropTypes.string,
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onFavorite: PropTypes.func.isRequired,
  onUnfavorite: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired,
  subheader: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
LoadCard.defaultProps = {
  driver: undefined,
};

export default LoadCard;
